import { getChannelDef } from "../saleHelpers/shared";
import { updateFulfillmentStatementFn } from "../../facade/ServiceFacade";

/**
 * The part of `helper/myOrderHelpers` that both platforms run.
 *
 * 🔴 **Why this folder exists at all:** `validateManualDiscountLimitFn` lives in a DIFFERENT
 * SOURCE FILE on each platform — web's is in `helper/saleHelpers.ts`, mobile's in
 * `helper/myOrderHelpers.ts` — and the mirror rule puts each copy in the core folder matching
 * its source. That split is STRUCTURAL, not behavioural: both bodies now read the same
 * `validateManualDiscountLimit` off `sale_util.js`. Converging it means relocating web's copy
 * out of `saleHelpers`, which is a separate move with its own importers — NOT done here.
 *
 * Not shared, and correctly so: `stopDesktopNotification` (index.desktop.ts) drives Electron's
 * `<audio>` or an NW.js IPC push. There is no desktop notification on a phone. PERMANENT.
 */

/**
 * Is this an advance order — does its channel allow advance orders AND does the order carry
 * the flag?
 *
 * SHARED as of the web-adoption work; it was `.desktop`-only. The old docstring recorded the
 * reason as *"WEB-ONLY: apps/mobile/src/helper/myOrderHelpers has no such function (grepped
 * repo-wide)"* — which is DEMAND-DRIVEN divergence (mobile never needed it yet), not PERMANENT
 * (mobile cannot do it). The body is two optional-chained property reads over `getChannelDef`,
 * which lives in `saleHelpers/shared` and resolves on both platforms. Nothing here is web-only.
 *
 * Imports `getChannelDef` from `saleHelpers/shared` rather than the folder: it is defined in the
 * SHARED half, so naming the file avoids a needless variant hop.
 *
 * ⚠️ Returns `channelDef.advanceOrders` (possibly `undefined`) when the order flag is absent —
 * it is NOT coerced to a boolean. Callers use it in a boolean position (`isAdvanceOrder(x) &&`),
 * so this is behaviour-preserving; do not "tidy" it into `!!`.
 */
export function isAdvanceOrder(orderData: any) {
  let channelDef = getChannelDef(orderData?.channelDefId);
  return channelDef?.advanceOrders && orderData?.deliveryInfo?.advanceOrder;
}

/**
 * ── Order fulfillment status ──────────────────────────────────────────────────────────────
 *
 * Pulled up by the `MyOrderCard` dependency closure: the card's status display needs these,
 * and **none of the three existed on mobile at all** — demand-driven, not permanent. All three
 * are pure: no store, no platform API, no `Date`. The two below `getOrderFulfillmentStatus`
 * have no dependencies whatsoever, just literals.
 *
 * Only `getFulfillmentMapping` is here: its bodies are IDENTICAL in both apps (checked, not
 * assumed). `getPickupOrderNextStatus` genuinely diverges and lives in the variants, and
 * `getOrderFulfillmentStatus` follows it there because it calls it — see either variant.
 *
 * ⚠️ `getFulfillmentMapping` returns `undefined` for an unrecognised action: no default case,
 * and deliberately distinct from the `""` its divergent sibling returns. Callers distinguish
 * them, so do not "tidy" the pair into one lookup table.
 */

/**
 * The status ladder lives in `./fulfillmentStatus` and is re-exported here, so every consumer
 * keeps importing `@rista/core/helpers/myOrderHelpers` exactly as before.
 *
 * ⚠️ It is a LEAF FILE on purpose, not a style choice. `isAdvanceOrder` above needs
 * `getChannelDef`, and `saleHelpers/shared` imports the whole `redux-store`, which reaches
 * `utils/localStorage` — a module that opens a SQLite file at load. That makes anything
 * importing this file unloadable in the node test harness, and the ladder is the half that
 * changed mobile's behaviour and therefore most needs a test. Splitting the pure half out is
 * what lets `myOrderHelpers.test.ts` import it without booting a store.
 */
export {
  getFulfillmentMapping,
  getPickupOrderNextStatus,
  getOrderFulfillmentStatus,
  getSendData,
  isDue,
  isPaid,
  isAllItemsVerified,
  isSourceDeliveredForced,
  isMarkedSoldOut,
  getFallbackNextAction,
  getResolvedNextAction,
  getFallbackActionDueTime,
  DEFAULT_ACCEPT_IN_SECONDS,
  DEFAULT_NEXT_ACTION_DUE_SECONDS,
} from "./fulfillmentStatus";
export { getParentOrderId, getOrderId, isOrderCod } from "./orderId";
export { isZomatoOrder, isSwiggyOrder, checkAPISupportOrder, isDAASOrderSource } from "./orderSource";
export {
  isRiderAssignAttempted,
  isRiderAssigned,
  getLatestRiderStatus,
  isRiderCancelled,
  isOrderDispatched,
  isSelfDeliveryOrder,
  countUnverifiedItems,
  isVerifiedStatus,
} from "./rider";


/**
 * Push a fulfillment-status change through the facade, which decides online vs offline.
 *
 * Uplifted 2026-09-08. The apps' bodies were IDENTICAL (both a one-line delegation with the same
 * dead `store.dispatch` comment above it), so nothing was converged — this is a straight move.
 *
 * ⚠️ It is a thin wrapper that exists only so call sites do not import the facade directly, and
 * it deliberately returns nothing: the result arrives through `payload.callback`.
 */
export function updateOrderFulfillmentStatus(payload: any) {
  updateFulfillmentStatementFn(payload);
  return;
}
